'use client'

import Image from "next/image"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import { Briefcase, CheckSquare, Megaphone, Palette, Users, Code, Server, Cog, Package, FileText, ShieldCheck, Pill, Scale, MessageCircle, PieChart, Smartphone } from "lucide-react"

// Define 9 work types mapped to available screenshots and routes
const workTypes = [
  { id: 'projects', label: 'Claims', image: '/images/dash/dash-main.png', route: '/project', icon: FileText },
  { id: 'tasks', label: 'Member', image: '/images/dash/dashboard-2.png', route: '/tasks', icon: Users },
  { id: 'marketing', label: 'Authorization', image: '/images/dash/dashboard-3.png', route: '/marketing', icon: ShieldCheck },
  { id: 'design', label: 'Provider', image: '/images/dash/d4.jpg', route: '/design', icon: Briefcase },
  { id: 'crm', label: 'Pharmacy', image: '/images/dash/d5.jpg', route: '/crm', icon: Pill },
  { id: 'software', label: 'Underwriting', image: '/images/dash/d6.jpg', route: '/software', icon: Scale },
  { id: 'it', label: 'CRM', image: '/images/dash/d7.jpg', route: '/it', icon: MessageCircle },
  { id: 'operations', label: 'Reporting', image: '/images/dash/d8.jpg', route: '/operations', icon: PieChart },
  { id: 'product', label: 'Mobile App', image: '/images/dash/dash-main.png', route: '/product', icon: Smartphone }
]

// 4 gradient themes to cycle on selection
const buttonColors = [
  'bg-gradient-to-r from-[#7c4599] via-blue-500 to-[#2cd0ba] text-white hover:brightness-110',
  'bg-gradient-to-r from-[#2cd0ba] via-teal-500 to-cyan-600 text-white hover:brightness-110',
  'bg-gradient-to-r from-[#e22951] via-fuchsia-500 to-pink-500 text-white hover:brightness-110',
  'bg-gradient-to-r from-[#edbe3c] via-amber-500 to-red-500 text-white hover:brightness-110'
]

export function HeroSection() {
  // No default selection per requirements; support multi-select
  const [selectedWork, setSelectedWork] = useState<string[]>([])
  const [hoveredWork, setHoveredWork] = useState<string | null>(null)
  const [currentMobileImage, setCurrentMobileImage] = useState(0)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  // Mobile auto-slide animation
  useEffect(() => {
    const isMobile = window.innerWidth < 768
    if (!isMobile) return

    const interval = setInterval(() => {
      setCurrentMobileImage(prev => (prev + 1) % workTypes.length)
    }, 3000) // Change image every 3 seconds

    return () => clearInterval(interval)
  }, [])

  // Toggle multi-select
  const toggleSelection = (workId: string) => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true)
    }
    setSelectedWork(prev => prev.includes(workId)
      ? prev.filter(id => id !== workId)
      : [...prev, workId]
    )
  }

  // Last selected determines link; keep CTA active with default route
  const lastSelectedId = selectedWork.length ? selectedWork[selectedWork.length - 1] : null
  const selectedRoute = lastSelectedId
    ? workTypes.find(w => w.id === lastSelectedId)?.route
    : '/project'

  const getButtonColor = () => {
    // Change color immediately on first selection for desktop
    if (hasUserInteracted && selectedWork.length > 0) {
      const idx = selectedWork.length % buttonColors.length
      return buttonColors[idx]
    }
    return buttonColors[0]
  }

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-[#2cd0ba]/10 overflow-hidden mb-[6vh] px-[5%] py-[5%]">
      {/* Header content – moved above background per request */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-gray-900">
            Run a Smarter, AI-Powered HMO with Octocare ERP
            <br />
            <span className="text-gray-600"></span>
          </h1>
          <p className="mt-6 text-[0.9rem] sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Transform your operations with one intelligent platform that automates underwriting, claims, authorizations, and pharmacy benefits all in real time.
          </p>

          <Button
            size="lg"
            className={`mt-8 px-3 py-2.5 rounded-full text-base font-medium shadow-lg hover:shadow-xl transition-all ${getButtonColor()}`}
            onClick={() => {
              window.location.href = selectedRoute || '/project'
            }}
          >
            Get Started →
          </Button>

          {/* <p className="mt-3 text-sm text-gray-500">
            No credit card needed • Unlimited time on Free plan
          </p> */}
        </div>
      </div>

      {/* Center background: widened by 15% (height fixed) below the header */}
      <div className="relative flex justify-center">
        <div className="relative mt-6 sm:mt-8 md:mt-10 h-[336px] sm:h-[432px] md:h-[552px] lg:h-[624px] w-[486px] sm:w-[626px] md:w-[800px] lg:w-[905px] rounded-2xl overflow-hidden ">
          {workTypes.map((work, index) => {
            // Mobile behavior: auto-slide and unblur
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
            const isCurrentMobileImage = isMobile && index === currentMobileImage
            const shouldShowOnMobile = isMobile && isCurrentMobileImage
            
            // Desktop behavior: hover to unblur
            const shouldShowOnDesktop = !isMobile && (
              hoveredWork
                ? hoveredWork === work.id
                : false
            )

            return (
              <div
                key={work.id}
                className={`absolute inset-0 transition-all duration-700 ${
                  isMobile
                    ? shouldShowOnMobile
                      ? 'opacity-100 blur-none z-10 transform translate-x-0'
                      : index < currentMobileImage
                        ? 'opacity-0 blur-none pointer-events-none transform -translate-x-full'
                        : 'opacity-0 blur-none pointer-events-none transform translate-x-full'
                    : hoveredWork
                      ? shouldShowOnDesktop
                        ? 'opacity-100 blur-none z-10'
                        : 'opacity-0 blur-none pointer-events-none'
                      : 'opacity-100 blur-[5px]'
                }`}
              >
                <Image
                  src={work.image}
                  alt={work.label}
                  fill
                  className="object-contain object-center"
                  priority={work.id === 'projects'}
                />
              </div>
            )
          })}
        </div>
      </div>

      {/* Overlay card: hidden on mobile, visible on desktop */}
      <div className="hidden md:block absolute right-6 md:right-10 top-[39%] translate-x-[-20%] translate-y-0 z-20 w-[280px] md:w-[320px]">
        <div className="bg-white/90 backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/60">
          <h3 className="text-lg font-medium text-gray-900 mb-4">What Feature Interest You?</h3>
          <div className="grid grid-cols-3 gap-2">
            {workTypes.map((work) => (
              <button
                key={work.id}
                className={`aspect-square p-2 flex flex-col items-center justify-center rounded-lg border-2 transition-all duration-200 text-xs font-medium ${
                  selectedWork.includes(work.id)
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white/80 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => toggleSelection(work.id)}
                onMouseEnter={() => setHoveredWork(work.id)}
                onMouseLeave={() => setHoveredWork(null)}
                aria-pressed={selectedWork.includes(work.id)}
              >
                <div className="flex flex-col items-center justify-center gap-1 text-center px-1">
                  {work.icon && (
                    <work.icon className={`w-5 h-5 ${selectedWork.includes(work.id) ? 'text-blue-600' : 'text-gray-500'}`} />
                  )}
                  <span className="truncate text-[11px] sm:text-xs">{work.label}</span>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    selectedWork.includes(work.id) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedWork.includes(work.id) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <Button
            className={`w-full mt-4 rounded-full px-3 py-2.5 text-base font-medium shadow-lg hover:shadow-xl transition-all ${getButtonColor()}`}
            onClick={() => {
              window.location.href = selectedRoute || '/project'
            }}
          >
            Get Started →
          </Button>
        </div>
      </div>
    </section>
  )
}
